import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser';

// SessionStorage key: invite email stored here before MSAL redirects
// AuthProvider reads this after MSAL completes to pre-fill login-sync
export const PENDING_INVITE_EMAIL_KEY = 'firesquirrel_pending_invite_email';

export default function AuthAcceptPage() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  const [statusText, setStatusText] = useState('Verifying your invitation...');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('No invitation token provided. Please use the link from your invitation email.');
      setProcessing(false);
      return;
    }

    handleInviteToken(token);
  }, []);

  const handleInviteToken = async (token: string) => {
    try {
      setStatusText('Verifying your invitation...');

      // Validate invite token with system-api (public endpoint — no auth bearer required)
      const res = await fetch(`/api/auth/validate-invite-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        throw new Error(errorBody.error || `Verification failed: ${res.status}`);
      }

      const { email, hasExistingAccount } = await res.json();

      console.log('[auth-accept] Invite validated:', { email, hasExistingAccount });

      // Store pending invite context for login-sync to pick up after MSAL completes
      sessionStorage.setItem(PENDING_INVITE_EMAIL_KEY, email);

      // Clear token from URL before redirecting (security — token in browser history)
      window.history.replaceState({}, document.title, '/auth/accept');

      setStatusText(hasExistingAccount
        ? 'Account found — redirecting you to sign in...'
        : 'Setting up your account — redirecting to registration...');

      // Small delay so the status message is visible
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Fetch External ID config (ciamlogin.com) for system admin sign-up
      // System admins use External ID same as tenant-web users
      const configRes = await fetch('/api/auth/external-id-config');
      if (!configRes.ok) {
        throw new Error('Failed to fetch authentication configuration');
      }
      const authConfig = await configRes.json();
      if (!authConfig.configured || !authConfig.clientId) {
        throw new Error('Authentication is not configured. Please contact your administrator.');
      }

      const msalConfig: Configuration = {
        auth: {
          clientId: authConfig.clientId,
          authority: authConfig.authority,
          redirectUri: authConfig.redirectUri || window.location.origin + '/auth/callback',
          postLogoutRedirectUri: window.location.origin,
          navigateToLoginRequestUrl: false,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
        system: {
          loggerOptions: {
            logLevel: LogLevel.Info,
            loggerCallback: (level, message) => {
              if (level <= LogLevel.Warning) console.log('[msal]', message);
            },
          },
        },
      };

      const msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();

      const scopes = authConfig.scopes || ['openid', 'profile', 'offline_access'];

      if (hasExistingAccount) {
        // Path B: existing External ID account — normal sign-in, pre-fill email
        await msalInstance.loginRedirect({
          scopes,
          loginHint: email,
          redirectUri: authConfig.redirectUri || window.location.origin + '/auth/callback',
        });
      } else {
        // Path A: new user — force sign-up flow
        // External ID supports 'prompt=create' but also try extraQueryParameters for screen_hint
        await msalInstance.loginRedirect({
          scopes,
          loginHint: email,
          prompt: 'create',
          extraQueryParameters: {
            screen_hint: 'signup',
          },
          redirectUri: authConfig.redirectUri || window.location.origin + '/auth/callback',
        });
      }
    } catch (err: any) {
      console.error('[auth-accept] Invite validation failed:', err);

      if (err.message?.includes('expired')) {
        setError('Your invitation has expired. Please contact your system administrator for a new invitation.');
      } else if (err.message?.includes('revoked')) {
        setError('This invitation has been revoked. Please contact your system administrator.');
      } else if (err.message?.includes('Invalid')) {
        setError('This invitation link is invalid. Please use the original link from your invitation email.');
      } else {
        setError(err.message || 'Failed to verify invitation. Please try again or contact support.');
      }

      setProcessing(false);
    }
  };

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: '#B22222', marginBottom: '16px', fontSize: '24px' }}>
            Authentication Error
          </h2>
          <p style={{ marginBottom: '24px', color: '#333', lineHeight: '1.6' }}>
            {error}
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '12px 24px',
              backgroundColor: '#ff6b35',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (processing) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: '#666', fontSize: '16px' }}>
            {statusText}
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return null;
}
